import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from '../api/axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [effectiveRoleOverride, setEffectiveRoleOverride] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get('/api/auth/me');
      setUser(response.data.user);
    } catch (error) {
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post('/api/auth/login', { email, password });
    const { token, user } = response.data;

    localStorage.setItem('token', token);

    // Clear service worker caches before setting user
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach(name => caches.delete(name));
      });
    }

    setEffectiveRoleOverride(null);
    setUser(user);
    return user;
  };

  const register = async (userData) => {
    const response = await axios.post('/api/auth/register', userData);
    const { token, user } = response.data;
    localStorage.setItem('token', token);
    setEffectiveRoleOverride(null);
    setUser(user);
    return user;
  };

  const logout = async () => {
    localStorage.removeItem('token');
    setEffectiveRoleOverride(null);
    setUser(null);
  };

  // effectiveRole: admin can override to test other role views; all others always use real role
  const effectiveRole = (user?.role === 'admin' && effectiveRoleOverride)
    ? effectiveRoleOverride
    : user?.role;

  const setEffectiveRole = (role) => {
    if (user?.role === 'admin') setEffectiveRoleOverride(role);
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    effectiveRole,
    setEffectiveRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
